import React, { createContext, useContext, useEffect, useState } from "react";
import moment from "moment";

import "../../styles/StackPlotEditor.css";
import { colors } from "../../styles/colors";
import { default as utilIcons } from "../../resources/utilIcons";

import StackPlot from "./StackPlot";
import { data as dummyData, config as dummyConfig } from "./StackPlotDummy";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { ModalContext, RestContext, UserContext } from "../../App";
import {
    ADMIN_URL,
    DATA_URL,
    SITE_URL,
    STACKPLOT_URL,
    UTIL_URL,
} from "../../serverConfig";
import MultiSelect from "../../components/MultiSelect";
import {
    checkCustomParameter,
    generateMockDataset,
} from "../../util/mockDataUtil";
import SearchableSelect from "../../components/SearchableSelect";
import { stopPropagation } from "ol/events/Event";

const StackPlotEditorContext = createContext(null);
const PermissionsContext = createContext(null);
const PermissionsModalContext = createContext(null);

const NUM_DAYS = 3;

const INTERVALS = {
    hourly: 60 * 60,
    "1-min": 60,
    "5-min": 60 * 5,
    "15-min": 60 * 15,
    daily: 60 * 60 * 24,
    cumulative: 60 * 60,
    "24-hr run": 60 * 60,
};

const PARAMETER_TYPES = [
    "L", // Line
    "O", // Line-Point, Circle
    "X", // Line-Point, X
    "T", // Line-Point, Triangle
    "o", // Point, Circle
    "x", // Point, X
    "t", // Point, Triangle
    "B", // Bar
];

function StackPlotEditor({ selectedSite }) {
    const [configList, setConfigList] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState();
    const [selectedConfigName, setSelectedConfigName] = useState("");
    const [config, setConfig] = useState({});
    const [data, setData] = useState({});
    const [mockDatasets, setMockDatasets] = useState({});
    const [columns, setColumns] = useState([]);
    const [selectedChartIndex, setSelectedChartIndex] = useState(null);
    const [siteParameterList, setSiteParameterList] = useState([]);
    const [precision, setPrecision] = useState();
    const [permissions, setPermissions] = useState({});
    const [changed, setChanged] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [headerOptionsOpen, setHeaderOptionsOpen] = useState(false);
    const [chartRefs, setChartRefs] = useState();

    const { sendGetRequest, sendPostRequest } = useContext(RestContext);
    const { openModal, setOpenModal } = useContext(ModalContext);
    const { user } = useContext(UserContext);

    const toggleHeaderOptionsOpen = () =>
        setHeaderOptionsOpen(!headerOptionsOpen);
    const toggleCreateModal = () => {
        if (openModal === "stackplot-create-modal") {
            setOpenModal();
        } else {
            setOpenModal("stackplot-create-modal");
        }
    };
    const toggleCopyModal = () => {
        if (openModal === "stackplot-copy-modal") {
            setOpenModal();
        } else {
            setOpenModal("stackplot-copy-modal");
        }
    };
    const toggleDeleteModal = () => {
        if (openModal === "stackplot-delete-modal") {
            setOpenModal();
        } else {
            setOpenModal("stackplot-delete-modal");
        }
    };
    const togglePermissionsModal = () => {
        if (openModal === "stackplot-permissions-modal") {
            setOpenModal();
        } else {
            setOpenModal("stackplot-permissions-modal");
        }
    };

    useEffect(() => {
        setConfig(dummyConfig);
        setColumns(dummyData.columns);
    }, []);

    useEffect(() => {
        loadStackplotList();
    }, [selectedSite]);

    useEffect(() => {
        if (selectedConfig) {
            const foundConfig = configList.find(
                (config) => config.stackPlotId === Number(selectedConfig)
            );
            if (foundConfig) setSelectedConfigName(foundConfig.plotName);
            sendGetRequest(
                STACKPLOT_URL + "/config/" + selectedConfig,
                (response) => {
                    setConfig(response.data);
                    setChanged(false);
                }
            );
            sendGetRequest(
                STACKPLOT_URL + "/permissions/" + selectedConfig,
                (response) => {
                    const newPermissions = {};
                    for (let permission of response.data.permissions) {
                        newPermissions[permission[0]] = permission[1];
                    }
                    setPermissions(newPermissions);
                }
            );
        } else {
            setSelectedConfigName("");
        }
    }, [selectedConfig]);

    useEffect(() => {
        if (config && selectedSite !== "all") {
            let newMockDatasets = { ...mockDatasets };
            if (config.precision) {
                setPrecision(config.precision);

                if (newMockDatasets.precision !== config.precision) {
                    newMockDatasets = { precision: config.precision };
                }
            }
            if (config.charts) {
                for (let chart of config.charts) {
                    if (chart.parameters) {
                        for (let par of chart.parameters) {
                            if (checkCustomParameter(par.parName)) {
                                if (!(par.parName in newMockDatasets)) {
                                    newMockDatasets[par.parName] =
                                        generateMockDataset(
                                            par.parName,
                                            NUM_DAYS,
                                            newMockDatasets.precision
                                        );
                                }
                            } else if (!("other" in newMockDatasets)) {
                                newMockDatasets["other"] = generateMockDataset(
                                    "other",
                                    NUM_DAYS,
                                    newMockDatasets.precision
                                );
                            }
                        }
                    }
                }
            }

            setMockDatasets(newMockDatasets);
        }
    }, [config, selectedSite]);

    useEffect(() => {
        if (selectedSite && precision && selectedConfig) {
            sendGetRequest(
                `${DATA_URL}/parameters/${selectedSite}?interval=${
                    isCalPlot() ? "hourly" : precision
                }`,
                (response) => {
                    setSiteParameterList(response.data);
                }
            );
        }
    }, [selectedSite, precision, selectedConfig]);

    useEffect(() => {
        if (mockDatasets && selectedSite !== "all") {
            setData(explodeMockData());
        }
    }, [mockDatasets]);

    useEffect(() => {
        if (chartRefs && selectedChartIndex !== null) {
            const oldBorder =
                document.querySelector("#selected-chart-border") || null;
            if (oldBorder) {
                oldBorder.remove();
            }
            const el =
                document.querySelector(
                    "#stackplot-chart-" + selectedChartIndex
                ) || null;
            if (el) {
                const newBorder = document.createElement("div");
                newBorder.id = "selected-chart-border";
                newBorder.style = {
                    position: "absolute",
                    top: -1,
                    height: `calc(100% + 2px)`,
                    width: `calc(100% + 2px)`,
                    border: "1px solid var(--ars-neutral-100)",
                    borderRadius: "6px",
                };
                el.appendChild(newBorder);
            }
        }
    }, [selectedChartIndex]);

    const loadStackplotList = (refresh = false) => {
        if (selectedSite) {
            if (selectedSite === "all") {
                setConfig(dummyConfig);
                setColumns(dummyData.columns);
                setData(explodeData(dummyData.data));
            } else {
                sendGetRequest(
                    STACKPLOT_URL + "/list/" + selectedSite,
                    (response) => {
                        if (response.data && response.data.length) {
                            const responseData = response.data.sort((a, b) =>
                                a.plotName < b.plotName ? -1 : 1
                            );
                            if (!refresh) {
                                setSelectedConfig(responseData[0].stackPlotId);
                                setSelectedConfigName(responseData[0].plotName);
                            }
                            setConfigList(responseData);
                        } else {
                            setConfigList([]);
                            setSelectedConfig();
                            setSelectedConfigName("");
                        }
                    }
                );
            }
        }
    };

    const explodeData = (rawData) => {
        if (!rawData || !columns) return null;
        const explodedData = {};
        for (let i = 0; i < columns.length; i += 2) {
            explodedData[columns[i]] = {
                columnIndex: i,
            };
        }

        const rawTimestamps = Object.keys(rawData).sort();
        let minTimestamp = Number(rawTimestamps[0]);
        let maxTimestamp = Number(rawTimestamps[rawTimestamps.length - 1]);
        let timestamps = [];
        let interval = 60;
        for (let i = 0; i <= (maxTimestamp - minTimestamp) / interval; i++) {
            timestamps.push(minTimestamp + i * interval);
        }

        for (let timestamp of timestamps) {
            let row = rawData[timestamp];
            if (row) {
                for (let parCode in explodedData) {
                    explodedData[parCode][timestamp] = [
                        row[explodedData[parCode].columnIndex],
                        row[explodedData[parCode].columnIndex + 1],
                    ];
                }
            }
        }
        explodedData._timestamps = timestamps;
        return explodedData;
    };

    const explodeMockData = () => {
        if (!config || !config.precision || !config.charts) return null;

        // Generate timestamps
        let timestamp = moment().minute(0).second(0).unix();
        let endTimestamp = moment().add(NUM_DAYS, "days").unix();
        let interval = INTERVALS[config.precision];
        let timestamps = [];
        while (timestamp < endTimestamp) {
            timestamps.push(timestamp);
            timestamp += interval;
        }

        // Prepare parameters to load mock data
        const parMockMappings = {};
        const otherParOffsets = {};
        let otherParCount = 0;
        for (let chart of config.charts) {
            for (let parameter of chart.parameters) {
                if (parameter.parCode in parMockMappings) {
                    continue;
                }
                if (checkCustomParameter(parameter.parName)) {
                    parMockMappings[parameter.parCode] = parameter.parName;
                } else {
                    parMockMappings[parameter.parCode] = "other";
                    otherParOffsets[parameter.parCode] = otherParCount * 5;
                    otherParCount++;
                }
            }
        }

        // Load mock data
        const explodedData = {};
        for (let parameter in parMockMappings) {
            explodedData[parameter] = {};
        }
        if (Object.keys(explodedData).length) {
            for (let parameter in explodedData) {
                const parOffset =
                    parameter in otherParOffsets
                        ? otherParOffsets[parameter]
                        : 0;
                const parDataset = mockDatasets[parMockMappings[parameter]];
                if (parDataset) {
                    for (let i in timestamps) {
                        explodedData[parameter][timestamps[i]] = [
                            mockDatasets[parMockMappings[parameter]][i] +
                                parOffset,
                            null,
                        ];
                    }
                }
            }
        }
        explodedData._timestamps = timestamps;
        return explodedData;
    };

    const getPlotName = () => {
        return config?.plotName || "";
    };

    const getFullTitle = () => {
        return config?.fullTitle || "";
    };

    const getFootnote = () => {
        return config?.footnote || "";
    };

    const getPrecision = () => {
        return config?.precision || "";
    };

    const getDataType = () => {
        return config?.dataType || "";
    };

    const getChartSpacing = () => {
        return Number(config?.chartSpacing || 0);
    };

    const getDefaultSpan = () => {
        return Number(config?.defaultSpan || 0);
    };

    const getChartConfig = () => {
        return config?.charts?.at(selectedChartIndex) || null;
    };

    const handleConfigSelect = (value) => {
        setSelectedConfig(value);
    };

    const handleInput = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        const newConfig = { ...config, [name]: value };
        if (
            event.target.name === "dataType" &&
            ["RAWCAL", "VALCAL"].includes(value)
        ) {
            newConfig.precision = "hourly";
        }
        setConfig(newConfig);
        setChanged(true);
    };

    const handleNumberInput = (event) => {
        const name = event.target.name;
        const value = Number(event.target.value);
        setConfig((values) => ({ ...values, [name]: value }));
        setChanged(true);
    };

    const updateChart = (index, chartConfig) => {
        const newCharts = [...config.charts];
        newCharts[index] = chartConfig;
        setConfig((values) => ({ ...values, charts: newCharts }));
        setChanged(true);
    };

    const updateParameter = (newParameter) => {
        if (selectedChartIndex === null || !config?.charts) return;
        const newChart = { ...config.charts[selectedChartIndex] };
        let par;
        for (let i = 0; i < newChart.parameters.length; i++) {
            par = newChart.parameters[i];
            if (par.parCode === newParameter.parCode) {
                newChart.parameters[i] = newParameter;
                updateChart(selectedChartIndex, newChart);
                return;
            }
        }
    };

    const plotEditorPlugin = {
        id: "plotEditorPlugin",
        beforeEvent: (chart, args) => {
            const event = args.event;
            if (event.type === "click") {
                setSelectedChartIndex(chart.options.chartIndex);
            }
        },
    };

    const getChartEditor = () => {
        if (!config?.dataType || selectedChartIndex === null) return null;
        if (config.dataType.match(/CAL/)) {
            return (
                <CalChartEditor
                    chartIndex={selectedChartIndex}
                    chartConfig={getChartConfig()}
                    updateChart={updateChart}
                />
            );
        } else {
            return (
                <ChartEditor
                    chartIndex={selectedChartIndex}
                    chartConfig={getChartConfig()}
                    updateChart={updateChart}
                />
            );
        }
    };

    const createNewChart = () => {
        if (!config?.charts) return;
        let newCharts = [...config.charts];
        let newChart = {
            leftMin: null,
            leftMax: null,
            rightMax: null,
            rightMin: null,
            chartHeight: 200,
            parameters: [],
            calType: config.dataType.match(/CAL/) ? "Span" : null,
        };
        newCharts.push(newChart);
        setConfig((values) => ({ ...values, charts: newCharts }));
        setChanged(true);
    };

    const deleteSelectedChart = () => {
        if (
            !(
                config?.charts ||
                selectedChartIndex === null ||
                config?.charts?.length >= selectedChartIndex
            )
        )
            return;
        let newCharts = config.charts
            .slice(0, selectedChartIndex)
            .concat(config.charts.slice(selectedChartIndex + 1));
        setConfig((values) => ({ ...values, charts: newCharts }));
        if (newCharts.length === 0) {
            setSelectedChartIndex(null);
        } else if (selectedChartIndex >= newCharts.length) {
            setSelectedChartIndex(newCharts.length - 1);
        }
        setChanged(true);
    };

    const swapCharts = (i, j) => {
        if (
            !config?.charts ||
            i > config.charts.length ||
            j > config.charts.length
        )
            return;
        const newCharts = config.charts;
        const temp = newCharts[i];
        newCharts[i] = newCharts[j];
        newCharts[j] = temp;
        setConfig((values) => ({ ...values, charts: newCharts }));
        setChanged(true);
    };

    const mapConfigsToOption = () => {
        if (!configList) return null;
        return configList.map((config, index) => (
            <option
                key={"stackplot-config-list-option-" + index}
                value={config.stackPlotId}
            >
                {config.plotName}
            </option>
        ));
    };

    const createStackplot = (plotName) => {
        const payload = { siteId: selectedSite, plotName: plotName };
        setWaiting(true);
        sendPostRequest(
            STACKPLOT_URL + "/create",
            payload,
            (response) => {
                setWaiting(false);
                setOpenModal();
                loadStackplotList(true);
            },
            (error) => {
                setWaiting(false);
            }
        );
    };

    const copyStackplot = (newPlotName, plotId) => {
        const payload = {
            newPlotName: newPlotName,
            plotId: plotId,
            siteId: Number(selectedSite),
        };
        setWaiting(true);
        sendPostRequest(
            STACKPLOT_URL + "/copy",
            payload,
            (response) => {
                setWaiting(false);
                setOpenModal();
                loadStackplotList(true);
            },
            (error) => {
                setWaiting(false);
            }
        );
    };

    const deleteStackplot = () => {
        setWaiting(true);
        sendPostRequest(
            STACKPLOT_URL + "/delete/" + selectedConfig,
            null,
            (response) => {
                setWaiting(false);
                setOpenModal();
                loadStackplotList();
            },
            (error) => {
                setWaiting(false);
            }
        );
    };

    const createPayload = () => {
        const payload = { ...config, idNo: selectedSite, charts: [] };
        if (isCalPlot()) {
            for (let chart of config.charts) {
                const newChart = {
                    ...chart,
                    parameters: chart.parameters.filter(
                        (parameter) => !parameter.valType.match(/DIFF/)
                    ),
                };
                payload.charts.push(newChart);
            }
        } else {
            payload.charts = config.charts;
        }
        return payload;
    };

    const handleSubmit = () => {
        setWaiting(true);

        const payload = createPayload();
        sendPostRequest(
            STACKPLOT_URL + "/config",
            payload,
            (response) => {
                setConfig(response.data);
                setWaiting(false);
                setChanged(false);
                loadStackplotList(true);
            },
            (error) => {
                setWaiting(false);
            }
        );
    };

    const isCalPlot = () => {
        return config?.dataType?.match(/CAL/)?.length > 0 || false;
    };

    const togglePermission = (userId) => {
        if (permissions[userId] === "R") {
            setPermissions((values) => ({ ...values, [userId]: "W" }));
        } else {
            setPermissions((values) => ({ ...values, [userId]: "R" }));
        }
        setChanged(true);
    };

    const isOwner = () => {
        if (!user?.userId || !permissions) return false;
        return permissions[user.userId] === "O";
    };

    const hasWrite = () => {
        if (!user?.userId || !permissions) return false;
        return (
            permissions[user.userId] === "O" || permissions[user.userId] === "W"
        );
    };

    return (
        <StackPlotEditorContext.Provider
            value={{ selectedSite, siteParameterList, config, updateParameter }}
        >
            <PermissionsContext.Provider
                value={{
                    permissions,
                    setPermissions,
                    togglePermission,
                    isOwner,
                    hasWrite,
                }}
            >
                <div id="stackplot-editor">
                    <div id="stackplot-editor-header">
                        <div className="stackplot-control-wrapper">
                            <Label>Select Configuration:</Label>

                            <SearchableSelect
                                id={"stackplot-config-select"}
                                data={configList}
                                selected={selectedConfig}
                                setSelected={handleConfigSelect}
                                valField={"stackPlotId"}
                                textField={"plotName"}
                                groupField={"dataType"}
                            />
                            <Button
                                id="stackplot-editor-create-button"
                                onClick={toggleCreateModal}
                            >
                                Create New
                            </Button>
                            <Dropdown
                                isOpen={headerOptionsOpen}
                                toggle={toggleHeaderOptionsOpen}
                            >
                                <DropdownToggle className="checklist-admin-options-toggle">
                                    {utilIcons.vDots(colors["ars-neutral-400"])}
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem onClick={toggleCopyModal}>
                                        Copy Stack Plot
                                    </DropdownItem>
                                    <DropdownItem
                                        style={{
                                            color:
                                                !selectedConfig || !isOwner()
                                                    ? colors["ars-neutral-600"]
                                                    : colors["ars-error"],
                                        }}
                                        onClick={toggleDeleteModal}
                                        disabled={!selectedConfig || !isOwner()}
                                    >
                                        Delete Stack Plot
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                    </div>
                    <div id="stackplot-editor-main">
                        <div id="stackplot-editor-left">
                            <div
                                id="stackplot-editor-left-block"
                                hidden={hasWrite()}
                            />
                            <div className="stackplot-editor-input-wrapper">
                                <Label>Full Title</Label>
                                <Input
                                    type="text"
                                    name="fullTitle"
                                    className="stackplot-editor-input"
                                    value={getFullTitle()}
                                    onChange={handleInput}
                                />
                            </div>
                            <div className="stackplot-editor-input-wrapper">
                                <Label>Plot Name</Label>
                                <Input
                                    type="text"
                                    name="plotName"
                                    className="stackplot-editor-input"
                                    value={getPlotName()}
                                    onChange={handleInput}
                                />
                            </div>
                            <div className="stackplot-editor-input-wrapper">
                                <Label>Default Data Span (days)</Label>
                                <Input
                                    type="number"
                                    name="defaultSpan"
                                    className="stackplot-editor-input"
                                    value={getDefaultSpan()}
                                    onChange={handleNumberInput}
                                />
                            </div>
                            <div className="stackplot-editor-input-wrapper">
                                <Label>Chart Spacing</Label>
                                <Input
                                    type="number"
                                    name="chartSpacing"
                                    className="stackplot-editor-input"
                                    value={getChartSpacing()}
                                    onChange={handleNumberInput}
                                />
                            </div>

                            <div className="stackplot-editor-input-wrapper">
                                <Label>Data Type</Label>
                                <select
                                    name="dataType"
                                    className="stackplot-editor-select"
                                    value={getDataType()}
                                    onChange={handleInput}
                                >
                                    <option value={"RAW"}>Raw</option>
                                    <option value={"VAL"}>Validated</option>
                                    <option value={"RAWCAL"}>
                                        Raw Calibration
                                    </option>
                                    <option value={"VALCAL"}>
                                        Validated Calibration
                                    </option>
                                    <option value={"VMPRI"}>
                                        VALIDATION MASTER - Primary
                                    </option>
                                    <option value={"VMCAL"}>
                                        VALIDATION MASTER - Calibration
                                    </option>
                                </select>
                            </div>
                            <div
                                className="stackplot-editor-input-wrapper"
                                hidden={isCalPlot()}
                            >
                                <Label>Data Precision</Label>
                                <select
                                    name="precision"
                                    className="stackplot-editor-select"
                                    value={getPrecision()}
                                    onChange={handleInput}
                                >
                                    <option value={"1-min"}>1 Minute</option>
                                    <option value={"5-min"}>5 Minute</option>
                                    <option value={"15-min"}>15 Minute</option>
                                    <option value={"hourly"}>Hourly</option>
                                    <option value={"daily"}>Daily</option>
                                    <option value={"cumulative"}>
                                        Cumulative
                                    </option>
                                    <option value={"24-hour run"}>
                                        24-Hour Run
                                    </option>
                                </select>
                            </div>
                            <div className="stackplot-editor-input-wrapper">
                                <Label>Footnote</Label>
                                <Input
                                    type="textarea"
                                    name="footnote"
                                    className="stackplot-editor-textarea"
                                    value={getFootnote()}
                                    onChange={handleInput}
                                />
                            </div>
                            <ChartSelect
                                config={config}
                                selectedIndex={selectedChartIndex}
                                setSelectedIndex={setSelectedChartIndex}
                                createNewChart={createNewChart}
                                deleteSelectedChart={deleteSelectedChart}
                                swapCharts={swapCharts}
                            />
                            <Button
                                id="stackplot-editor-permissions-toggle"
                                disabled={!isOwner()}
                                onClick={togglePermissionsModal}
                            >
                                Edit Permissions
                            </Button>
                            <Button
                                className="submit-button"
                                disabled={!changed}
                                onClick={handleSubmit}
                            >
                                {waiting ? (
                                    <Spinner size="sm" />
                                ) : (
                                    "Save Changes"
                                )}
                            </Button>
                        </div>
                        <div id="stackplot-editor-center">
                            <StackPlot
                                data={data}
                                config={config}
                                plugins={[plotEditorPlugin]}
                                bubbleChartRefs={setChartRefs}
                            />
                        </div>
                        <div id="stackplot-editor-right">
                            <div
                                id="stackplot-editor-right-block"
                                hidden={hasWrite()}
                            />
                            {getChartEditor()}
                        </div>
                    </div>

                    <StackPlotCreateModal
                        isOpen={openModal === "stackplot-create-modal"}
                        toggle={toggleCreateModal}
                        createStackplot={createStackplot}
                        waiting={waiting}
                    />
                    <StackPlotCopyModal
                        isOpen={openModal === "stackplot-copy-modal"}
                        toggle={toggleCopyModal}
                        copyStackplot={copyStackplot}
                        waiting={waiting}
                    />
                    <StackPlotDeleteModal
                        isOpen={openModal === "stackplot-delete-modal"}
                        toggle={toggleDeleteModal}
                        deleteStackplot={deleteStackplot}
                        plotName={selectedConfigName}
                        waiting={waiting}
                    />
                    <StackPlotPermissionsModal
                        isOpen={openModal === "stackplot-permissions-modal"}
                        toggle={togglePermissionsModal}
                        configId={selectedConfig}
                    />
                </div>
            </PermissionsContext.Provider>
        </StackPlotEditorContext.Provider>
    );
}

function ChartSelect({
    config,
    selectedIndex,
    setSelectedIndex,
    createNewChart,
    deleteSelectedChart,
    swapCharts,
}) {
    const mapCharts = () => {
        if (!config?.charts?.length) return null;
        return config.charts.map((chart, index) => (
            <ChartSelectRow
                chart={chart}
                index={index}
                selected={selectedIndex === index}
                setSelectedIndex={setSelectedIndex}
            />
        ));
    };

    const handleDown = () => {
        swapCharts(selectedIndex, selectedIndex + 1);
        setSelectedIndex(selectedIndex + 1);
    };

    const handleUp = () => {
        swapCharts(selectedIndex - 1, selectedIndex);
        setSelectedIndex(selectedIndex - 1);
    };

    const getChartsLength = () => {
        return config?.charts?.length || 0;
    };

    return (
        <div id="stackplot-editor-chart-select">
            <div id="stackplot-editor-chart-select-label">
                <Label>Charts</Label>
                <Button
                    className="stackplot-editor-label-button"
                    onClick={handleDown}
                    disabled={
                        selectedIndex === null ||
                        selectedIndex === getChartsLength() - 1
                    }
                >
                    {utilIcons.caret(colors["ars-neutral-900"])}
                </Button>
                <Button
                    className="stackplot-editor-label-button"
                    onClick={handleUp}
                    disabled={selectedIndex === null || selectedIndex === 0}
                >
                    {utilIcons.caret(colors["ars-neutral-900"], {
                        rotate: "180deg",
                    })}
                </Button>
                <Button
                    className="stackplot-editor-label-button"
                    onClick={createNewChart}
                >
                    <b>+</b>
                </Button>
                <Button
                    className="stackplot-editor-label-button"
                    onClick={deleteSelectedChart}
                    disabled={selectedIndex === null}
                >
                    <b>-</b>
                </Button>
            </div>
            <div id="chart-container-wrapper">
                <div id="chart-container">{mapCharts()}</div>
            </div>
        </div>
    );
}

function ChartSelectRow({ chart, index, selected, setSelectedIndex }) {
    const handleClick = (event) => {
        setSelectedIndex(index);
    };

    const getChartText = () => {
        if (!chart?.parameters?.length)
            return (
                <span style={{ color: colors["ars-neutral-600"] }}>Empty</span>
            );
        return chart.parameters.map((parameter) => parameter.parCode).join(" ");
    };

    return (
        <div
            className={"chart-select-row" + (selected ? " selected" : "")}
            onClick={handleClick}
        >
            <span>{getChartText()}</span>
            <span className="chart-select-row-index">{`{${
                index !== undefined && index !== null ? index + 1 : 0
            }}`}</span>
        </div>
    );
}

function ChartEditor({ chartIndex, chartConfig, updateChart }) {
    const hasRightSide = () => {
        return chartConfig?.hasRightSide || false;
    };

    const hasCustomLeftScale = () => {
        return !(
            chartConfig?.leftMin === null ||
            chartConfig?.leftMin === undefined ||
            chartConfig?.leftMax === null ||
            chartConfig?.leftMax === undefined
        );
    };

    const hasCustomRightScale = () => {
        return !(
            chartConfig?.rightMin === null ||
            chartConfig?.rightMin === undefined ||
            chartConfig?.rightMax === null ||
            chartConfig?.rightMax === undefined
        );
    };

    const getChartHeight = () => {
        return chartConfig?.chartHeight || 200;
    };

    const getLeftMin = () => {
        return chartConfig?.leftMin || 0;
    };

    const getLeftMax = () => {
        return chartConfig?.leftMax || 100;
    };

    const getRightMin = () => {
        return chartConfig?.rightMin || 0;
    };

    const getRightMax = () => {
        return chartConfig?.rightMax || 100;
    };

    const handleNumberInput = (event) => {
        const name = event.target.name;
        const value = Number(event.target.value);
        const newChartConfig = { ...chartConfig, [name]: value };
        updateChart(chartIndex, newChartConfig);
    };

    const toggleHasRightSide = () => {
        if (!chartConfig) return;
        updateChart(chartIndex, {
            ...chartConfig,
            hasRightSide: chartConfig.hasRightSide ? false : true,
        });
    };

    const toggleCustomLeftScale = () => {
        if (!chartConfig) return;
        if (hasCustomLeftScale()) {
            updateChart(chartIndex, {
                ...chartConfig,
                leftMin: null,
                leftMax: null,
            });
        } else {
            updateChart(chartIndex, {
                ...chartConfig,
                leftMin: 0,
                leftMax: 100,
            });
        }
    };

    const toggleCustomRightScale = () => {
        if (!chartConfig) return;
        if (hasCustomRightScale()) {
            updateChart(chartIndex, {
                ...chartConfig,
                rightMin: null,
                rightMax: null,
            });
        } else {
            updateChart(chartIndex, {
                ...chartConfig,
                rightMin: 0,
                rightMax: 100,
            });
        }
    };

    const updateParameters = (sideParameters, side) => {
        let newParameters =
            chartConfig?.parameters?.filter(
                (parameter) => parameter.side !== side
            ) || [];
        for (let parameter of sideParameters) {
            parameter.color = parameter.color || "000000";
            parameter.side = parameter.side || side;
            parameter.type = parameter.type || "line";
            parameter.unit = parameter.unit || parameter.unitCode;
            parameter.parName = parameter.parName || parameter.parCode;
            parameter.flagName =
                parameter.flagName || parameter.parName + "_FLAG";
            parameter.symbol = parameter.symbol || "L";
        }
        newParameters.push(...sideParameters);
        const newChartConfig = { ...chartConfig, parameters: newParameters };
        updateChart(chartIndex, newChartConfig);
    };

    return (
        <>
            <div className="stackplot-editor-input-wrapper">
                <Label>Chart Height</Label>
                <Input
                    type="number"
                    name="chartHeight"
                    className="stackplot-editor-input"
                    value={getChartHeight()}
                    onChange={handleNumberInput}
                />
            </div>
            <div className="stackplot-editor-section">
                <Label>Left Side</Label>

                <div className="stackplot-editor-input-wrapper inline">
                    <Label>Custom Scale?</Label>
                    <Input
                        type="checkbox"
                        className="stackplot-editor-checkbox"
                        checked={hasCustomLeftScale()}
                        onClick={toggleCustomLeftScale}
                        readOnly
                    />
                </div>

                <div
                    className="stackplot-editor-input-wrapper"
                    hidden={!hasCustomLeftScale()}
                >
                    <div className="stackplot-editor-number-range">
                        <Input
                            type="number"
                            name="leftMin"
                            className="stackplot-editor-input"
                            value={getLeftMin()}
                            onChange={handleNumberInput}
                        />
                        <span>-</span>
                        <Input
                            type="number"
                            name="leftMax"
                            className="stackplot-editor-input"
                            value={getLeftMax()}
                            onChange={handleNumberInput}
                        />
                    </div>
                </div>
                <div className="stackplot-editor-input-wrapper">
                    <ParameterSelect
                        chartConfig={chartConfig}
                        side={"L"}
                        updateParameters={updateParameters}
                    />
                </div>
            </div>
            <div className="stackplot-editor-input-wrapper inline">
                <Label>Has Right Side?</Label>
                <Input
                    type="checkbox"
                    className="stackplot-editor-checkbox"
                    checked={hasRightSide()}
                    onClick={toggleHasRightSide}
                    readOnly
                />
            </div>
            <div className="stackplot-editor-section" hidden={!hasRightSide()}>
                <Label>Right Side</Label>
                <div className="stackplot-editor-input-wrapper inline">
                    <Label>Custom Scale?</Label>
                    <Input
                        type="checkbox"
                        className="stackplot-editor-checkbox"
                        checked={hasCustomRightScale()}
                        onClick={toggleCustomRightScale}
                        readOnly
                    />
                </div>
                <div
                    className="stackplot-editor-input-wrapper"
                    hidden={!hasCustomRightScale()}
                >
                    <div className="stackplot-editor-number-range">
                        <Input
                            type="number"
                            name="rightMin"
                            className="stackplot-editor-input"
                            value={getRightMin()}
                            onChange={handleNumberInput}
                        />
                        <span>-</span>
                        <Input
                            type="number"
                            name="rightMax"
                            className="stackplot-editor-input"
                            value={getRightMax()}
                            onChange={handleNumberInput}
                        />
                    </div>
                </div>
                <div className="stackplot-editor-input-wrapper">
                    <ParameterSelect
                        chartConfig={chartConfig}
                        side={"R"}
                        updateParameters={updateParameters}
                    />
                </div>
            </div>
        </>
    );
}

function CalChartEditor({ chartIndex, chartConfig, updateChart }) {
    const { siteParameterList } = useContext(StackPlotEditorContext);

    const hasCustomLeftScale = () => {
        return !(
            chartConfig?.leftMin === null ||
            chartConfig?.leftMin === undefined ||
            chartConfig?.leftMax === null ||
            chartConfig?.leftMax === undefined
        );
    };

    const getChartHeight = () => {
        return chartConfig?.chartHeight || 200;
    };

    const getCalType = () => {
        return chartConfig?.calType || null;
    };

    const getLeftMin = () => {
        return chartConfig?.leftMin || 0;
    };

    const getLeftMax = () => {
        return chartConfig?.leftMax || 100;
    };

    const handleNumberInput = (event) => {
        const name = event.target.name;
        const value = Number(event.target.value);
        const newChartConfig = { ...chartConfig, [name]: value };
        updateChart(chartIndex, newChartConfig);
    };

    const toggleCustomLeftScale = () => {
        if (!chartConfig) return;
        if (hasCustomLeftScale()) {
            updateChart(chartIndex, {
                ...chartConfig,
                leftMin: null,
                leftMax: null,
            });
        } else {
            updateChart(chartIndex, {
                ...chartConfig,
                leftMin: 0,
                leftMax: 100,
            });
        }
    };

    const handleCalType = (event) => {
        const value = event.target.value;
        const newChartConfig = { ...chartConfig, calType: value };
        for (let parameter of newChartConfig.parameters) {
            parameter.calType = value;
        }
        updateChart(chartIndex, newChartConfig);
    };

    const getIndicatedParameter = () => {
        return getParameter("INDICATED");
    };
    const getActualParameter = () => {
        return getParameter("ACTUAL");
    };

    const getParameter = (valType) => {
        const indicatedParameter =
            chartConfig?.parameters?.find(
                (parameter) => parameter.valType === valType
            ) || null;
        return indicatedParameter?.parCode || null;
    };

    const getIndicatedColor = () => {
        return getColor("INDICATED");
    };

    const getActualColor = () => {
        return getColor("ACTUAL");
    };

    const getColor = (valType) => {
        const indicatedParameter =
            chartConfig?.parameters?.find(
                (parameter) => parameter.valType === valType
            ) || null;
        return "#" + (indicatedParameter?.color || "000000");
    };

    const getIndicatedPrecision = () => {
        return getPrecision("INDICATED");
    };

    const getActualPrecision = () => {
        return getPrecision("ACTUAL");
    };

    const getPrecision = (valType) => {
        const indicatedParameter =
            chartConfig?.parameters?.find(
                (parameter) => parameter.valType === valType
            ) || null;
        return indicatedParameter?.rawPrec;
    };

    const handleIndicated = (value) => {
        handleParameterSelect(value, "INDICATED");
    };

    const handleActual = (value) => {
        handleParameterSelect(value, "ACTUAL");
    };

    const handleParameterSelect = (value, valType) => {
        const foundParameter = siteParameterList.find(
            (parameter) => parameter.parCode === value
        );
        if (!foundParameter) return;

        const newParameters = [...chartConfig.parameters];
        const existingParameter = newParameters.find(
            (parameter) => parameter.valType === valType
        );
        if (existingParameter) {
            existingParameter.parCode = value;
            existingParameter.unitCode = foundParameter.unitCode;
        } else {
            const newParameter = {
                parCode: foundParameter.parCode,
                color: "000000",
                side: "L",
                type: "point",
                unit: foundParameter.unitCode,
                parName: foundParameter.parCode,
                symbol: "o",
                calType: chartConfig.calType,
                valType: valType,
            };
            newParameters.push(newParameter);
        }
        const newChartConfig = { ...chartConfig, parameters: newParameters };
        updateChart(chartIndex, newChartConfig);
    };

    const handleIndicatedColor = (event) => {
        handleColor(event, "INDICATED");
    };

    const handleActualColor = (event) => {
        handleColor(event, "ACTUAL");
    };

    const handleColor = (event, valType) => {
        const value = event.target.value.replace("#", "");

        const newParameters = [...chartConfig.parameters];
        const existingParameter = newParameters.find(
            (parameter) => parameter.valType === valType
        );
        if (!existingParameter) return;
        existingParameter.color = value;

        const newChartConfig = { ...chartConfig, parameters: newParameters };
        updateChart(chartIndex, newChartConfig);
    };

    const handleIndicatedPrecision = (event) => {
        handlePrecision(event, "INDICATED");
    };

    const handleActualPrecision = (event) => {
        handlePrecision(event, "ACTUAL");
    };

    const handlePrecision = (event, valType) => {
        const value = event.target.value;

        const newParameters = [...chartConfig.parameters];
        const existingParameter = newParameters.find(
            (parameter) => parameter.valType === valType
        );
        if (!existingParameter) return;
        existingParameter.rawPrec = value;

        const newChartConfig = { ...chartConfig, parameters: newParameters };
        updateChart(chartIndex, newChartConfig);
    };

    return (
        <>
            <div className="stackplot-editor-input-wrapper">
                <Label>Chart Height</Label>
                <Input
                    type="number"
                    name="chartHeight"
                    className="stackplot-editor-input"
                    value={getChartHeight()}
                    onChange={handleNumberInput}
                />
            </div>
            <div className="stackplot-editor-input-wrapper">
                <Label>Cal Type</Label>
                <select
                    name="calType"
                    className="stackplot-editor-select"
                    value={getCalType()}
                    onChange={handleCalType}
                >
                    <option>Span</option>
                    <option>Zero</option>
                    <option>Prec</option>
                </select>
            </div>
            <div className="stackplot-editor-input-wrapper">
                <Label>Indicated</Label>
                <div className="stackplot-editor-input-wrapper inline">
                    <SearchableSelect
                        data={siteParameterList}
                        selected={getIndicatedParameter()}
                        setSelected={handleIndicated}
                        textField={"parCode"}
                        valField={"parCode"}
                    />
                    <span className="cal-parameter-precision-span">Prec</span>
                    <input
                        className="cal-parameter-precision-input"
                        type="number"
                        step={1}
                        min={0}
                        max={6}
                        value={getIndicatedPrecision()}
                        onChange={handleIndicatedPrecision}
                    />
                    <input
                        className="selected-parameter-color"
                        type="color"
                        value={getIndicatedColor()}
                        onChange={handleIndicatedColor}
                    />
                </div>
            </div>
            <div className="stackplot-editor-input-wrapper">
                <Label>Actual</Label>
                <div className="stackplot-editor-input-wrapper inline">
                    <SearchableSelect
                        data={siteParameterList}
                        selected={getActualParameter()}
                        setSelected={handleActual}
                        textField={"parCode"}
                        valField={"parCode"}
                    />
                    <span className="cal-parameter-precision-span">Prec</span>
                    <input
                        className="cal-parameter-precision-input"
                        type="number"
                        step={1}
                        min={0}
                        max={6}
                        value={getActualPrecision()}
                        onChange={handleActualPrecision}
                    />
                    <input
                        className="selected-parameter-color"
                        type="color"
                        value={getActualColor()}
                        onChange={handleActualColor}
                    />
                </div>
            </div>
            <div className="stackplot-editor-input-wrapper inline">
                <Label>Custom Scale?</Label>
                <Input
                    type="checkbox"
                    className="stackplot-editor-checkbox"
                    checked={hasCustomLeftScale()}
                    onClick={toggleCustomLeftScale}
                    readOnly
                />
            </div>
            <div
                className="stackplot-editor-input-wrapper"
                hidden={!hasCustomLeftScale()}
            >
                <div className="stackplot-editor-number-range">
                    <Input
                        type="number"
                        name="leftMin"
                        className="stackplot-editor-input"
                        value={getLeftMin()}
                        onChange={handleNumberInput}
                    />
                    <span>-</span>
                    <Input
                        type="number"
                        name="leftMax"
                        className="stackplot-editor-input"
                        value={getLeftMax()}
                        onChange={handleNumberInput}
                    />
                </div>
            </div>
        </>
    );
}

function ParameterSelect({ chartConfig, side, updateParameters }) {
    const { siteParameterList } = useContext(StackPlotEditorContext);

    const getSelectedParameters = () => {
        if (!chartConfig?.parameters?.length) return [];
        return chartConfig.parameters.filter(
            (parameter) => parameter.side === side
        );
    };

    const setSelected = (sideParameters) => {
        updateParameters(sideParameters, side);
    };

    return (
        <MultiSelect
            className="stackplot-editor-parameter-select"
            data={siteParameterList}
            selected={getSelectedParameters()}
            setSelected={setSelected}
            textField={"parCode"}
            valField={"parCode"}
            hideSearch={true}
            UnselectedComponent={UnselectedParameter}
            SelectedComponent={SelectedParameter}
        />
    );
}

function UnselectedParameter({ item, className, value, ...props }) {
    const getText = () => {
        return item?.parCode || "";
    };

    return (
        <div
            className={
                "multiselect-option" +
                className +
                (item.isMostRecent ? " recent" : "")
            }
            value={value}
            {...props}
        >
            {getText(item)}
        </div>
    );
}

function SelectedParameter({ item, className, value, ...props }) {
    const { updateParameter } = useContext(StackPlotEditorContext);

    const getText = () => {
        return item?.parCode || "No ParCode";
    };

    const getColor = () => {
        return "#" + (item?.color || "000000");
    };

    const getType = () => {
        return item?.type || null;
    };

    const getSymbol = () => {
        return item?.symbol || null;
    };

    const getPrecision = () => {
        return item ? item.rawPrec : null;
    };

    const handleColor = (event) => {
        const value = event.target.value.replace("#", "");
        const newParameter = { ...item, color: value };
        updateParameter(newParameter);
    };

    const handleType = (symbol) => {
        let type;
        if (symbol === "L") {
            type = "line";
        } else if (symbol === "B") {
            type = "bar";
        } else if (symbol.match(/^[A-Z]$/)) {
            type = "line-point";
        } else {
            type = "point";
        }
        const newParameter = { ...item, type: type, symbol: symbol };
        updateParameter(newParameter);
    };

    const handlePrecisionClick = (event) => {
        event.stopPropagation();
    };

    const handlePrecision = (event) => {
        const value = Number(event.target.value);
        const newParameter = { ...item, rawPrec: value };
        updateParameter(newParameter);
    };

    return (
        <div
            className={
                "multiselect-option selected-parameter" +
                (className ? " " + className : "")
            }
            value={value}
            {...props}
        >
            <ParameterTypeToggle
                currentType={getType()}
                currentSymbol={getSymbol()}
                handleType={handleType}
            />
            <div className={"selected-parameter-code"}>{getText()}</div>
            <input
                className="selected-parameter-precision-input"
                type="number"
                step={1}
                min={0}
                max={6}
                value={getPrecision()}
                onClick={stopPropagation}
                onChange={handlePrecision}
            />
            <input
                className="selected-parameter-color"
                type="color"
                value={getColor()}
                onChange={handleColor}
            />
        </div>
    );
}

function ParameterTypeToggle({ currentType, currentSymbol, handleType }) {
    const getCurrentType = () => {
        return currentSymbol || " ";
    };

    const nextType = () => {
        if (!currentType) {
            handleType("L");
        }
        const currentIndex = PARAMETER_TYPES.indexOf(currentSymbol);
        if (currentIndex === -1) {
            handleType("L");
        } else {
            const nextSymbol =
                PARAMETER_TYPES[(currentIndex + 1) % PARAMETER_TYPES.length];
            handleType(nextSymbol);
        }
    };

    const prevType = (event) => {
        event.preventDefault();
        if (!currentType) {
            handleType("L");
        }
        const currentIndex = PARAMETER_TYPES.indexOf(currentSymbol);
        if (currentIndex === -1) {
            handleType("L");
        } else {
            if (currentIndex === 0) {
                handleType("B");
            } else {
                const prevSymbol = PARAMETER_TYPES[currentIndex - 1];
                handleType(prevSymbol);
            }
        }
    };

    return (
        <div
            className="selected-parameter-type"
            onClick={nextType}
            onAuxClick={prevType}
            onContextMenu={(event) => {
                event.preventDefault();
            }}
        >
            {getCurrentType()}
        </div>
    );
}

function StackPlotCreateModal({ isOpen, toggle, createStackplot }) {
    const [nameString, setNameString] = useState("");

    const handleChange = (event) => {
        setNameString(event.target.value);
    };

    const handleSubmit = (event) => {
        createStackplot(nameString);
    };

    const handleClosed = () => {
        setNameString("");
    };

    return (
        <Modal
            className="checklist-create-modal"
            isOpen={isOpen}
            toggle={toggle}
            onClosed={handleClosed}
        >
            <ModalHeader toggle={toggle}>Create New Stack Plot</ModalHeader>
            <ModalBody>
                <div className="checklist-modal-field-wrapper">
                    <Label>Plot Name</Label>
                    <Input
                        value={nameString}
                        onChange={handleChange}
                        placeholder="Plot name..."
                    />
                </div>
                <Button className="submit-button" onClick={handleSubmit}>
                    Create
                </Button>
            </ModalBody>
        </Modal>
    );
}

function StackPlotCopyModal({ isOpen, toggle, copyStackplot, waiting }) {
    const [plotName, setPlotName] = useState("");
    const [selectedSite, setSelectedSite] = useState();
    const [siteList, setSiteList] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState();
    const [configList, setConfigList] = useState([]);
    const { sendGetRequest, sendPostRequest } = useContext(RestContext);

    useEffect(() => {
        sendGetRequest(SITE_URL + "/list", (response) => {
            setSiteList(response.data);
        });
    }, []);

    useEffect(() => {
        if (selectedSite) {
            sendGetRequest(
                STACKPLOT_URL + "/list/" + selectedSite,
                (response) => {
                    if (response.data && response.data.length) {
                        setConfigList(response.data);
                    } else {
                        setConfigList([]);
                    }
                    setSelectedConfig();
                }
            );
        } else {
            setConfigList([]);
            setSelectedConfig();
        }
    }, [selectedSite]);

    const init = () => {
        setPlotName("");
        setSelectedSite();
    };

    const handleChange = (event) => {
        setPlotName(event.target.value);
    };

    const mapSitesToOption = () => {
        if (!siteList) return null;
        return siteList.map((site, index) => (
            <option
                key={"stackplot-copy-site-list-option-" + index}
                value={site.siteId}
            >
                {site.siteAbbr}
            </option>
        ));
    };

    const mapConfigsToOption = () => {
        if (!configList) return null;
        return configList
            .sort((a, b) => (a.plotName < b.plotName ? -1 : 1))
            .map((config, index) => (
                <div
                    key={"stackplot-copy-config-list-option-" + index}
                    className={
                        "stackplot-copy-config-list-option" +
                        (selectedConfig === config.stackPlotId
                            ? " selected"
                            : "")
                    }
                    onClick={() => handleClick(config)}
                >
                    {config.plotName}
                </div>
            ));
    };

    const handleSelect = (event) => {
        const value = event.target.value;
        setSelectedSite(value);
    };

    const handleClick = (config) => {
        setSelectedConfig(config.stackPlotId);
        setPlotName(config.plotName + " - Copy");
    };

    const handleSubmit = () => {
        if (waiting || !selectedConfig || !plotName?.length) return;
        copyStackplot(plotName, selectedConfig);
    };

    return (
        <Modal
            id="stackplot-copy-modal"
            className="checklist-copy-modal"
            isOpen={isOpen}
            toggle={toggle}
            onOpened={init}
        >
            <ModalHeader toggle={toggle}>Copy Stack Plot</ModalHeader>
            <ModalBody>
                <Label>Select Site To Copy From</Label>
                <select onChange={handleSelect}>{mapSitesToOption()}</select>
                <Label>Select Plot To Copy</Label>
                <div id="stackplot-copy-config-list-wrapper">
                    <div id="stackplot-copy-config-list-container">
                        {mapConfigsToOption()}
                    </div>
                </div>
                <Label>New Plot Name</Label>
                <Input
                    type="text"
                    value={plotName}
                    onChange={handleChange}
                    placeholder="New Plot Name..."
                />
                <Button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={!selectedConfig || !plotName?.length}
                >
                    {waiting ? <Spinner size="sm" /> : "Submit"}
                </Button>
            </ModalBody>
        </Modal>
    );
}

function StackPlotDeleteModal({ isOpen, toggle, plotName, deleteStackplot }) {
    const handleSubmit = () => {
        deleteStackplot();
    };

    return (
        <Modal
            className="checklist-delete-modal"
            isOpen={isOpen}
            toggle={toggle}
        >
            <ModalBody>
                <div>
                    {"Are you sure you want to permanently delete "}
                    <b>{plotName}</b>
                    {"?"}
                </div>
                <div className="checklist-delete-modal-buttons">
                    <Button
                        className="checklist-delete-modal-delete"
                        onClick={handleSubmit}
                    >
                        Delete
                    </Button>
                    <Button
                        className="checklist-delete-modal-cancel"
                        onClick={toggle}
                    >
                        Cancel
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );
}

function StackPlotPermissionsModal({ configId, isOpen, toggle }) {
    const [userList, setUserList] = useState([]);
    const [changed, setChanged] = useState(false);

    const { permissions, setPermissions, togglePermission } =
        useContext(PermissionsContext);
    const { sendGetRequest, sendPostRequest } = useContext(RestContext);
    const { selectedSite } = useContext(StackPlotEditorContext);

    useEffect(() => {
        if (isOpen) {
            sendGetRequest(UTIL_URL + "/users/" + selectedSite, (response) => {
                setUserList(response.data);
            });
            sendGetRequest(
                STACKPLOT_URL + "/permissions/" + configId,
                (response) => {
                    const newPermissions = {};
                    for (let permission of response.data.permissions) {
                        newPermissions[permission[0]] = permission[1];
                    }
                    setPermissions(newPermissions);
                    setChanged(false);
                }
            );
        }
    }, [isOpen]);

    const getSelectedUsers = () => {
        return userList.filter((user) => user.userId in permissions);
    };

    const handleSelected = (selectedList) => {
        const newPermissions = {};
        for (let user of selectedList) {
            const userId = user.userId;
            newPermissions[userId] = permissions[userId] || "R";
        }
        setPermissions(newPermissions);
        setChanged(true);
    };

    const handleSubmit = () => {
        const payload = { stackPlotId: configId, permissions: [] };
        for (let userId of Object.keys(permissions)) {
            payload.permissions.push([String(userId), permissions[userId]]);
        }
        sendPostRequest(STACKPLOT_URL + "/permissions", payload, (response) => {
            setChanged(false);
        });
    };

    const selectAllARS = () => {
        const newPermissions = { ...permissions };
        for (let user of userList) {
            if (["ROLE_ARS", "ROLE_ADMIN"].includes(user.userRole)) {
                const userId = user.userId;
                newPermissions[userId] = permissions[userId] || "R";
            }
        }
        setPermissions(newPermissions);
        setChanged(true);
    };

    const selectAll = () => {
        const newPermissions = { ...permissions };
        for (let user of userList) {
            const userId = user.userId;
            newPermissions[userId] = permissions[userId] || "R";
        }
        setPermissions(newPermissions);
        setChanged(true);
    };

    const deselectAll = () => {
        const newPermissions = {};
        for (let userId of Object.keys(permissions)) {
            if (permissions[userId] == "O") {
                newPermissions[userId] = "O";
                break;
            }
        }
        setPermissions(newPermissions);
        setChanged(true);
    };

    return (
        <PermissionsModalContext.Provider value={{ setChanged }}>
            <Modal isOpen={isOpen} toggle={toggle}>
                <ModalHeader toggle={toggle}>Edit Permissions</ModalHeader>
                <ModalBody>
                    <MultiSelect
                        data={userList}
                        textField={"userName"}
                        valField={"userId"}
                        selected={getSelectedUsers()}
                        setSelected={handleSelected}
                        SelectedComponent={StackPlotPermission}
                    />
                    <div id="stackplot-permissions-batch-controls">
                        <div className="left">
                            <Button onClick={selectAllARS}>All ARS</Button>
                            <Button onClick={selectAll}>Select All</Button>
                        </div>
                        <div className="right">
                            <Button onClick={deselectAll}>Remove All</Button>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        id="stackplot-permissions-modal-submit"
                        disabled={!changed}
                        onClick={handleSubmit}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </Modal>
        </PermissionsModalContext.Provider>
    );
}

function StackPlotPermission({ item, className, value, ...props }) {
    const { permissions, togglePermission } = useContext(PermissionsContext);
    const { setChanged } = useContext(PermissionsModalContext);

    const getUserFullName = () => {
        return item?.userName || "";
    };

    const getAccessLevel = () => {
        return permissions[item.userId] || "R";
    };

    const isOwner = () => {
        return getAccessLevel() == "O";
    };

    const handlePermission = (event) => {
        event.preventDefault();
        event.stopPropagation();
        togglePermission(item.userId);
        setChanged(true);
    };

    return (
        <div
            className={
                "multiselect-option stackplot-permission" +
                (className ? " " + className : "")
            }
            value={value}
            style={
                isOwner()
                    ? {
                          pointerEvents: "none",
                          color: colors["ars-neutral-600"],
                      }
                    : null
            }
            {...props}
        >
            <div className="stackplot-permission-name">{getUserFullName()}</div>
            <div
                className="stackplot-permission-access-level"
                onClick={handlePermission}
            >
                {getAccessLevel()}
            </div>
        </div>
    );
}

export default StackPlotEditor;
